import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash, faDownload, faImage, faUpload, faSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import { faSparkles as faSparklesSolid } from '@fortawesome/pro-solid-svg-icons';
import {
  Button, Modal, Form, Spinner,
} from 'react-bootstrap';
import { get } from 'lodash';

import RoundButton from '../../../components/RoundButton';
import SectionSelector from './SectionSelector';
import previewImage from '../../../assets/images/preview.jpg';
import imgPlaceholder from '../../../assets/images/image-placeholder.svg';

import { removeOrigin } from '../../../helpers/urls';
import { displayErrorModal } from '../../../helpers/swal';
import { uploadOverlayAnimationMedia } from './api';
import { hasTransparentBackground } from './helpers';

import style from './style.module.scss';

const MAX_ANIMATION_WEBP_BYTES = 2 * 1024 * 1024;
const MAX_ANIMATION_VIDEO_BYTES = 6 * 1024 * 1024;

const Overlay = ({
  addMode,
  loading,
  hide,
  overlay,
  slugDate,
  openDialog,
  onChange,
  onDelete,
}) => {
  const [animationModalOpen, setAnimationModalOpen] = useState(false);
  const [webpFile, setWebpFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [animationUploading, setAnimationUploading] = useState(false);
  const [webpInputKey, setWebpInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(0);
  const [webpPickPreview, setWebpPickPreview] = useState(null);
  const [videoPickPreview, setVideoPickPreview] = useState(null);
  const webpFileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const hasAnimation = Boolean(overlay?.animationURL || overlay?.animationVideoURL);

  useEffect(() => {
    if (!webpFile) {
      setWebpPickPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(webpFile);
    setWebpPickPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [webpFile]);

  useEffect(() => {
    if (!videoFile) {
      setVideoPickPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(videoFile);
    setVideoPickPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  const closeAnimationModal = useCallback(() => {
    setAnimationModalOpen(false);
    setWebpFile(null);
    setVideoFile(null);
    setWebpInputKey((k) => k + 1);
    setVideoInputKey((k) => k + 1);
  }, []);

  const clearWebpPick = useCallback(() => {
    setWebpFile(null);
    setWebpInputKey((k) => k + 1);
  }, []);

  const clearVideoPick = useCallback(() => {
    setVideoFile(null);
    setVideoInputKey((k) => k + 1);
  }, []);

  const handleWebpDelete = useCallback(() => {
    if (webpFile) {
      clearWebpPick();
    } else if (overlay?.animationURL) {
      onChange({ animationURL: null });
    }
  }, [webpFile, overlay, clearWebpPick, onChange]);

  const handleVideoDelete = useCallback(() => {
    if (videoFile) {
      clearVideoPick();
    } else if (overlay?.animationVideoURL) {
      onChange({ animationVideoURL: null });
    }
  }, [videoFile, overlay, clearVideoPick, onChange]);

  const submitAnimation = useCallback(async () => {
    if (!slugDate || !overlay?.storageKey) {
      displayErrorModal({
        title: 'Upload failed',
        text: 'Missing event or overlay.',
      });
      return;
    }
    if (!webpFile || !videoFile) {
      displayErrorModal({
        title: 'Missing files',
        text: 'Please choose both an animated WebP and a video file.',
      });
      return;
    }
    if (webpFile.size > MAX_ANIMATION_WEBP_BYTES) {
      displayErrorModal({
        title: 'File too large',
        text: 'Animated WebP must be 2 MB or less.',
      });
      return;
    }
    if (videoFile.size > MAX_ANIMATION_VIDEO_BYTES) {
      displayErrorModal({
        title: 'File too large',
        text: 'Video must be 6 MB or less.',
      });
      return;
    }

    try {
      const hasAlpha = await hasTransparentBackground(webpFile);
      if (!hasAlpha) {
        displayErrorModal({
          title: 'WebP not transparent',
          text: 'Please use an animated WebP with alpha (transparency).',
        });
        return;
      }
    } catch (err) {
      displayErrorModal({
        title: 'Invalid WebP',
        text: err.message || 'Could not read the WebP file.',
      });
      return;
    }

    setAnimationUploading(true);
    try {
      const { animationURL, animationVideoURL } = await uploadOverlayAnimationMedia(
        slugDate,
        overlay.storageKey,
        webpFile,
        videoFile,
      );
      onChange({ animationURL, animationVideoURL });
      closeAnimationModal();
    } catch (err) {
      const message = get(err, 'body.error') || err.message;
      displayErrorModal({
        title: 'Animation upload failed',
        text: message,
      });
    } finally {
      setAnimationUploading(false);
    }
  }, [slugDate, overlay, webpFile, videoFile, onChange, closeAnimationModal]);

  if (addMode) {
    return (
      <div className={classnames(style.emptyContainer, { [style.hide]: hide })}>
        <div className={style.overlayPreview}>
          {loading ? (
            <div className={style.overlayPreviewSpinner} role="status" aria-label="Loading">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className={style.emptyContent}>
              <div className={style.imageIcon}><FontAwesomeIcon icon={faImage} /></div>
              <ul className="small text-muted mb-0 pl-3">
                <li>PNG (8 bits - png8)</li>
                <li>Transparent background</li>
                <li>2160 x 2160 px or 2160 x 2700 px</li>
                <li>File size: 800 KB or less</li>
              </ul>
              <Button
                variant="outline-primary"
                onClick={openDialog}
                disabled={hide && loading}
                className="small mt-3"
              >
                Upload
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const fieldIdSuffix = String(overlay.storageKey).replace(/[^a-zA-Z0-9_-]/g, '_');

  const webpDisplaySrc = webpPickPreview || overlay.animationURL || null;
  const videoDisplaySrc = videoPickPreview || overlay.animationVideoURL || null;

  return (
    <div className={style.overlayContainer}>
      <div className={style.overlayPreview}>
        <img src={overlay.animationURL || overlay.urlFullSize} alt="" style={{ backgroundImage: `url(${previewImage})` }} />
      </div>
      <div className={style.controls}>
        <Button variant="link pr-0">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="switcher labelFirst mr-0">
            <input
              type="checkbox"
              defaultChecked={overlay.disabled}
              className="switcher-input"
              onChange={() => onChange({ disabled: !overlay.disabled })}
            />
            <span className="switcher-indicator">
              <span className="switcher-yes" />
              <span className="switcher-no" />
            </span>
            <span className="switcher-label">Disabled</span>
          </label>
        </Button>
        <a className="ml-auto" href={removeOrigin(overlay.urlRaw)} download>
          <FontAwesomeIcon icon={faDownload} size="sm" />
        </a>
        <button
          type="button"
          className={classnames({ [style.hasAnimation]: hasAnimation })}
          title="Animated overlay"
          aria-label="Animated overlay"
          onClick={() => setAnimationModalOpen(true)}
        >
          <FontAwesomeIcon
            icon={hasAnimation ? faSparklesSolid : faSparkles}
            size="sm"
          />
        </button>
        <button
          type="button"
          className={style.deleteButton}
          onClick={() => onDelete({ deleted: true })}
          title="Delete"
          aria-label="Delete"
        >
          <FontAwesomeIcon icon={faTrash} size="sm" />
        </button>
        <SectionSelector
          onChange={(sections) => onChange({ sections })}
          preSelected={overlay.sections}
        />
      </div>

      <Modal
        show={animationModalOpen}
        onHide={closeAnimationModal}
        centered
        className={style.animationModal}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h3">Animated overlay</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Give this overlay motion. Guests see the preview in the app; the video is used when we
            {' '}
            composite the final image.
          </p>

          <p className="small font-weight-bold mb-3">
            {hasAnimation ? 'Replace files' : 'Upload files'}
          </p>
          <Form.Group as="div" className={style.uploadRow}>
            <div className={style.uploadRowFields}>
              <Form.Label htmlFor={`overlay-animation-webp-${fieldIdSuffix}`}>
                Animated WebP (preview)
              </Form.Label>
              <input
                ref={webpFileInputRef}
                id={`overlay-animation-webp-${fieldIdSuffix}`}
                key={`webp-${webpInputKey}`}
                type="file"
                accept="image/webp"
                className="sr-only"
                onChange={(e) => setWebpFile(e.target.files?.[0] || null)}
              />
              <Form.Text as="div" className="text-muted">
                <ul className="small mb-0 pl-3">
                  <li>Transparent animated WebP</li>
                  <li>Max 2 MB</li>
                </ul>
              </Form.Text>
              <div className={style.uploadRowActions}>
                <div className={style.animationActionButtons}>
                  <RoundButton
                    title="Upload"
                    className={style.animationActionBtn}
                    onClick={() => webpFileInputRef.current?.click()}
                  >
                    <FontAwesomeIcon icon={faUpload} className="align-middle" />
                  </RoundButton>
                  {overlay.animationURL && !webpPickPreview && (
                    <a
                      className={style.animationActionBtn}
                      title="Download"
                      href={removeOrigin(overlay.animationURL)}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon={faDownload} className="align-middle" />
                    </a>
                  )}
                  {(webpFile || overlay.animationURL) && (
                    <RoundButton
                      title="Delete"
                      className={style.animationActionBtn}
                      onClick={handleWebpDelete}
                    >
                      <FontAwesomeIcon icon={faTrash} className="align-middle" />
                    </RoundButton>
                  )}
                </div>
              </div>
            </div>
            <div className={style.uploadRowPreview}>
              <div className={style.uploadRowPreviewMedia}>
                {webpDisplaySrc ? (
                  <img src={webpDisplaySrc} alt="" />
                ) : (
                  <img className={style.fieldPlaceholder} src={imgPlaceholder} alt="" />
                )}
              </div>
            </div>
          </Form.Group>
          <Form.Group as="div" className={classnames(style.uploadRow, 'mb-0')}>
            <div className={style.uploadRowFields}>
              <Form.Label htmlFor={`overlay-animation-video-${fieldIdSuffix}`}>
                Video (HEVC)
              </Form.Label>
              <input
                ref={videoFileInputRef}
                id={`overlay-animation-video-${fieldIdSuffix}`}
                key={`video-${videoInputKey}`}
                type="file"
                accept="video/quicktime,video/mp4,.mov,.mp4"
                className="sr-only"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              />
              <Form.Text as="div" className="text-muted">
                <ul className="small mb-0 pl-3">
                  <li>MOV or MP4 for compositing</li>
                  <li>Max 6 MB</li>
                </ul>
              </Form.Text>
              <div className={style.uploadRowActions}>
                <div className={style.animationActionButtons}>
                  <RoundButton
                    title="Upload"
                    className={style.animationActionBtn}
                    onClick={() => videoFileInputRef.current?.click()}
                  >
                    <FontAwesomeIcon icon={faUpload} className="align-middle" />
                  </RoundButton>
                  {overlay.animationVideoURL && !videoPickPreview && (
                    <a
                      className={style.animationActionBtn}
                      title="Download"
                      href={removeOrigin(overlay.animationVideoURL)}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon={faDownload} className="align-middle" />
                    </a>
                  )}
                  {(videoFile || overlay.animationVideoURL) && (
                    <RoundButton
                      title="Delete"
                      className={style.animationActionBtn}
                      onClick={handleVideoDelete}
                    >
                      <FontAwesomeIcon icon={faTrash} className="align-middle" />
                    </RoundButton>
                  )}
                </div>
              </div>
            </div>
            <div className={style.uploadRowPreview}>
              <div className={style.uploadRowPreviewMedia}>
                {videoDisplaySrc ? (
                  <video src={videoDisplaySrc} muted playsInline preload="metadata" />
                ) : (
                  <img className={style.fieldPlaceholder} src={imgPlaceholder} alt="" />
                )}
              </div>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={submitAnimation}
            disabled={animationUploading || !webpFile || !videoFile}
          >
            {animationUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Overlay;
